import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../../utils/seo';

// ===== Organization (EN mirror) — full rebuild, Implementer C scope =====
// Mirrors the JA organization page design language (navy #1a2942 / brass #a38e65 / BTN-08 / AboutSideNav /
//   editorial officer roster, 22px-class serif for roles & department names, hairline alignment, no card sprawl).
//   Facts from the same canon (mng-thebutler/00_business-overview + current JA organization working tree =
//   internal-completion text). No invented figures; no revived external claims. No Japanese characters (SEO rule 1-3).

export default function EnAboutOrganizationPage() {
  useEffect(() => {
    updateSEO(seoData['/en/about/organization']);
    document.documentElement.lang = 'en';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us', href: '/en/about' },
    { label: 'Organization' }
  ];

  const aboutNav = [
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const officers = [
    {
      role: 'President', roman: 'I.', name: 'Yuichi Ishii', sub: 'Third President',
      desc: 'Determines the overall management policy of the association and oversees the development of butler culture. As its external representative, he advances cooperation with related institutions and works to raise the social value of the butler profession.',
    },
    {
      role: 'Vice President', roman: 'II.', name: 'Management Advisor', sub: 'Advisor',
      desc: 'Supports the President and underpins the foundations of the association. Drawing on many years of experience, he provides guidance and coordinates and supervises the various initiatives, contributing to stable operation.',
    },
  ];

  const departments = [
    { no: '01', name: 'Education & Training', tags: 'Curriculum · Instructor development', desc: 'Handles the systematization of butler knowledge and skill and the development and delivery of training programs, while continually improving course content.' },
    { no: '02', name: 'Certification', tags: 'Examination · Continuing education', desc: 'Sets the standards and conducts the assessment for butler qualifications. Through continuing-education management and quality control, it protects the credibility of the credentials.' },
    { no: '03', name: 'International Exchange', tags: 'Global readiness · Language training', desc: 'Develops butlers who meet international standards, researches readiness to serve guests from overseas, and studies butler cultures abroad.' },
    { no: '04', name: 'Public Relations', tags: 'Outreach · Communications', desc: 'Handles publicity for the association, website operations, and media relations, working to spread butler culture and raise its profile.' },
    { no: '05', name: 'Administration', tags: 'General affairs · Membership', desc: 'Runs day-to-day operations, membership, finance, and general affairs, functioning as the base that supports the work of every department.' },
    { no: '06', name: 'Advisory Board', tags: 'Expert counsel · Strategy', desc: 'An advisory body of specialists from each field. Drawing on their experience and insight, they provide strategic counsel for the association.' },
  ];

  const chartUnits = ['Education & Training', 'Certification', 'International Exchange', 'Public Relations', 'Administration', 'Advisory Board'];

  const activities = [
    { name: 'Education & Training', desc: 'Systematizes butler knowledge and develops a stepwise learning curriculum. It produces materials for each level — Associate, Professional, and Master Butler — plans hands-on training programs, and develops instructors. Specialist curricula in dining, travel, and business manners are also maintained to support the diverse career paths of learners.' },
    { name: 'Certification', desc: 'Sets and periodically reviews the standards for butler qualifications, operating a consistent certification system. It maintains a fair and rigorous assessment framework — writing examinations, setting marking criteria, and managing practical tests. It also manages post-qualification continuing education so credential holders keep the latest knowledge and skill.' },
    { name: 'International Exchange', desc: 'Researches butler cultures and service trends abroad and applies them to developing butlers who meet international standards. It develops curricula covering international protocol and cross-cultural understanding, strengthens readiness to serve guests from overseas, and builds the framework to welcome learners from abroad.' },
    { name: 'Public Relations', desc: 'Communicates the association’s activities widely and works to raise awareness of butler culture. Through website operations, social media, media relations, and press releases, it runs outreach across many channels. It also introduces the achievements of credential holders and plans events that share butler culture with the public.' },
    { name: 'Administration', desc: 'Handles the foundational work that keeps the association running smoothly. It oversees membership registration, learner databases, financial management, legal matters, and general affairs. Supporting every department across the board, it strengthens the association’s operational base through efficient workflows and the management of facilities and equipment.' },
  ];

  const features = [
    { title: 'Focus on Expertise', desc: 'A committee system of specialists from each field takes part in running the association, so its high-quality service reflects the latest developments in the industry.' },
    { title: 'International Readiness', desc: 'Valuing an international outlook, we develop butlers able to serve guests from overseas, researching international standards and applying them to our programs.' },
    { title: 'Continuous Improvement', desc: 'Through regular review and improvement, we keep our operation flexible and responsive to changing times, aiming for sustainable growth that meets members’ needs.' },
    { title: 'Transparency', desc: 'We value transparency in operations and report our activities regularly. We fulfill our accountability to members and society and practice trustworthy governance.' },
  ];

  return (
    <Layout>

      {/* Hero — full-width billboard (BB-02 style) */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-p-010-org-staff.webp"
          alt="Specialists running the association in concert, the leaders of each field working together"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Organization</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">An Organization</span>
              <span className="block">Built on Expertise</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              The Japan Butler Association is run by the leaders of each specialist field working in concert, dedicated to the development of butler culture and the delivery of high-quality education. Under our board of directors, six departments function together to bring the highest standard of service to our members and learners.
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side nav + content (2 columns) */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/en/about/organization" eyebrow="About Us" heading="About the Association" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* I. Lead + editorial data row */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Governance</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  A body of specialists, <span className="text-[#a38e65]">working in concert</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <p>
                    The Japan Butler Association is supported by a structure in which the leaders of each specialist field work together. Under a board of directors headed by the President, six departments — from education and training to certification, international exchange, public relations, and administration — each bring their own expertise to bear.
                  </p>
                  <p>
                    We also draw on external cooperation such as our registered-instructor system, expert advisors, and outsourcing partners, achieving high-quality operation while remaining lean. The departments do not work in isolation; they cooperate closely under a shared philosophy.
                  </p>
                  <p>
                    This organizational form is the foundation for delivering a stable, top-tier service to our members and learners at all times. The strength of an organization, we believe, is greater than the sum of its individuals.
                  </p>
                </div>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Departments</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">6</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Officers</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Presidents</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Base</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">Odaiba, Tokyo</dd>
                  </div>
                </dl>
              </section>

              {/* II. Board of Directors — officer roster (new variation ①) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Board of Directors</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Board &amp; Officers
                </h2>
                <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
                  <div className="border-t-2 border-[#1a2942]">
                    {officers.map((o) => (
                      <div key={o.role} className="border-b border-[#e2ddd2] py-7">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm">{o.roman}</span>
                          <span className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em]">{o.role}</span>
                        </div>
                        <div className="flex items-baseline gap-4 mb-4">
                          <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-[28px] leading-tight">{o.name}</h3>
                          <span className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm">{o.sub}</span>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638] leading-[1.9] max-w-2xl">{o.desc}</p>
                      </div>
                    ))}
                  </div>
                  <div className="lg:pt-1">
                    <img
                      src="/images/generated/jba-p-011-org-board-v3.webp"
                      alt="A board meeting deliberating on the management policy of the association"
                      className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                </div>
              </section>

              {/* III. Departments — hairline numbered roster (new variation ②) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Departments</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Six Departments
                </h2>
                <img
                  src="/images/generated/jba-fs-018-about_organization-v3.webp"
                  alt="Staff collaborating across departments, a structure that draws on each field of expertise"
                  className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
                />
                <div className="border-t-2 border-[#1a2942]">
                  {departments.map((d) => (
                    <div key={d.no} className="flex flex-col sm:flex-row gap-3 sm:gap-8 border-b border-[#e2ddd2] py-6">
                      <div className="sm:w-[300px] sm:shrink-0 flex items-baseline gap-4">
                        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{d.no}</span>
                        <div>
                          <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] leading-tight mb-1.5">{d.name}</h3>
                          <span className="block font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.04em]">{d.tags}</span>
                        </div>
                      </div>
                      <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{d.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* IV. Structure — hierarchical chart */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Structure</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Organizational Chart
                </h2>
                <div className="border border-[#e2ddd2] bg-[#f8f6f0] px-6 sm:px-10 py-12 sm:py-14">
                  <div className="flex flex-col items-center">
                    <div className="bg-[#1a2942] text-center px-8 sm:px-12 py-5 min-w-[200px]">
                      <span className="block font-['Noto_Serif_JP',serif] text-[11px] text-[#d7b779] tracking-[0.12em] mb-1">President</span>
                      <span className="jba-display font-medium text-white text-lg sm:text-xl">Yuichi Ishii</span>
                    </div>
                    <div className="w-px h-8 bg-[#a38e65]"></div>
                    <div className="bg-[#a38e65] text-center px-7 sm:px-10 py-4 min-w-[180px]">
                      <span className="block font-['Noto_Serif_JP',serif] text-[11px] text-white/80 tracking-[0.12em] mb-0.5">Vice President</span>
                      <span className="jba-display font-medium text-white text-base sm:text-lg">Management Advisor</span>
                    </div>
                    <div className="w-px h-8 bg-[#a38e65]"></div>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-3 border-t border-l border-[#a38e65]/30">
                    {chartUnits.map((u) => (
                      <div key={u} className="border-r border-b border-[#a38e65]/30 bg-white px-4 py-5 text-center flex items-center justify-center">
                        <span className="jba-display font-medium text-[#1a2942] text-sm sm:text-[15px] leading-snug">{u}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-10 pt-8 border-t border-[#a38e65]/25">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg text-center mb-6">External Cooperation</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {[
                        { t: 'Registered Instructors', d: 'A faculty of practitioners with field experience in each specialty' },
                        { t: 'Expert Advisors', d: 'Guidance and counsel from specialists in each field' },
                        { t: 'Outsourcing Partners', d: 'External partners that support efficient operation' },
                      ].map((x) => (
                        <div key={x.t} className="border-t border-[#a38e65]/40 pt-4 text-center">
                          <h4 className="jba-display font-medium text-[#1a2942] text-base mb-1.5">{x.t}</h4>
                          <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#4a4638] leading-[1.8]">{x.d}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* V. What each team does — left-rule editorial blocks */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. What Each Team Does</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  What Each Department Does
                </h2>
                <img
                  src="/images/generated/jba-p-012-org-training.webp"
                  alt="Department staff engaged in training and education activities"
                  className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10"
                />
                <div className="space-y-6">
                  {activities.map((a) => (
                    <div key={a.name} className="border-l-2 border-[#a38e65] bg-[#f8f6f0] pl-6 sm:pl-8 pr-5 sm:pr-8 py-6">
                      <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] mb-3 leading-[1.4]">{a.name}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{a.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* VI. Our approach — navy 2x2 grid */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Our Approach</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  How We Operate
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-10 lg:px-14 py-12 sm:py-14">
                  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9">
                    {features.map((f, i) => (
                      <div key={f.title} className="border-t border-[#a38e65]/40 pt-5">
                        <div className="flex items-baseline gap-3 mb-2.5">
                          <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm">{`0${i + 1}`}</span>
                          <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl">{f.title}</h3>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.9]">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* VII. Next Steps — navy CTA band */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Developing Butler Culture, Together
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    The Japan Butler Association welcomes those with an interest in butler culture and those aiming to become professionals. Please feel free to reach out about our activities or our certification system.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/en/about/activities" className={BTN08_ON_NAVY}>Our Activities</a>
                    <a href="/en/qualifications" className={BTN08_ON_NAVY}>Certifications</a>
                    <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
